import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid } from "@material-ui/core";
import useIsMounted from "ismounted";
import { FC, useCallback, useContext, useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { NumberField } from "../../../../../../../components/forms/number";
import { TextField } from "../../../../../../../components/forms/text";
import { Flex } from "../../../../../../../components/layout";
import { useAppLoader } from "../../../../../../../components/loader/app-loader-hook";
import { StepperContext } from "../../../../../../../components/stepper/context";
import { VerificationDTO } from "../../../../../../../store/verification/models/dto/due-diligence";
import { VerificationService } from "../../../../../../../store/verification/service";
import { colours } from "../../../../../../../styles/colours";
import { Container } from "../../../common/components";
import { ConfigContext } from "../../../common/config-context";
import Participants from "./participants";

type FormType = VerificationDTO.Requests.DueDiligence.Form.Post.Init.T;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  align-items: center;
  grid-row-gap: 2em;
  grid-column-gap: 2em;

  & > *:nth-child(2n + 1) {
    text-align: right;
  }
`;

const Header = styled(Flex).attrs({ center: true })`
  font-weight: 500;
  padding: 2em 0;
  margin-top: 5em;
  border-top: 1px solid ${colours.lightGrey};
`;

export interface IInitialiseFormProps {
  configId: string;
}

export const InitialiseForm: FC<IInitialiseFormProps> = ({ configId }) => {
  const isMounted = useIsMounted();
  const loader = useAppLoader();
  const {
    state: { setKycToFillIn, setDueDiligenceId, setContactEmail },
  } = useContext(ConfigContext);
  const { setValidation } = useContext(StepperContext);

  const {
    validation,
  } = VerificationDTO.Requests.DueDiligence.Form.Post.Init.useFields(configId);

  const form = useForm<FormType>({
    resolver: yupResolver(validation),
    defaultValues: {
      participants: [],
      kycsToInput: 0,
    },
  });

  const handleSubmitCallback = useCallback(
    async (data: FormType) => {
      if (configId === undefined) throw new Error("");
      setKycToFillIn(data.kycsToInput - data.participants.length);
      setContactEmail(data.contact.emailAddress);
      const { id } = await loader(
        VerificationService.postDueDiligenceFormInit(configId, data)
      );
      setDueDiligenceId(id);
    },
    [loader, configId]
  );

  /** Set up the validation for the form */
  useEffect(() => {
    setValidation(() => () =>
      new Promise<void>((res, rej) => {
        form.handleSubmit((d) =>
          handleSubmitCallback(d).then(res).catch(rej)
        )();
      })
    );
    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [
    handleSubmitCallback,
    setValidation,
    form.handleSubmit,
    isMounted.current,
  ]);

  const { t } = useTranslation("fields");

  return (
    <Box component='div' sx={{ bgcolor: 'rgb(240, 248, 255)', borderRadius: '5px', padding: '1.4em' }}>
      <Container>
        <Grid item xs={12}>
          <Header>Company representative details</Header>
        </Grid>
        <Wrapper>
          {t("firstName")}
          <Controller
            control={form.control}
            name="contact.firstName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                error={!!error}
                helperText={error?.message}
                type="text"
                label=""
              />
            )}
          />

          {t("middleName")}
          <Controller
            control={form.control}
            name="contact.middleName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                error={!!error}
                helperText={error?.message}
                type="text"
                label=""
              />
            )}
          />

          {t("lastName")}
          <Controller
            control={form.control}
            name="contact.lastName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                error={!!error}
                helperText={error?.message}
                type="text"
                label=""
              />
            )}
          />

          {t("phone")}
          <Controller
            control={form.control}
            name="contact.phone"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                error={!!error}
                helperText={error?.message}
                type="text"
                label=""
              />
            )}
          />

          {t("emailAddress")}
          <Controller
            control={form.control}
            name="contact.emailAddress"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                error={!!error}
                helperText={error?.message}
                type="email"
                label=""
              />
            )}
          />
        </Wrapper>
        <FormProvider {...form}>
          <Participants />
        </FormProvider>
      </Container>
    </Box>
  );
};
